import { useRef, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Find Your Space',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Single static feature image that straddles the photo band and the white band
// on the right (desktop). Bound to the same property featured image as the hero
// background so the two stay visually matched.
const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_featured_image_media.alt_text',
      defaultValue: 'Property hero image',
    },
  },
  generator: {
    prompt:
      'Use the same photograph as the hero background / property featured image so the feature frame matches the main hero band. Prefer that featured community photo. Never pick parking lots, garages, EV charging stations, logos, floor plans, or utility spaces.',
    context:
      'Single static hero feature image (not a slider). It must match the hero background image shown in this same Hero section.',
    sourceCollection: 'photo_gallery',
  },
};

// Static feature image — mirrors the hero background media so the straddling
// frame matches the photo band. Falls back to the slot value when the
// background image is empty.
function FeatureImage({
  backgroundImage,
  backgroundAlt,
}: {
  backgroundImage?: string | null;
  backgroundAlt?: string | null;
}) {
  const slot = useSlotGroup('hero.feature_image', featureImageDef);

  const image = backgroundImage || slot.image || null;
  const alt = backgroundImage
    ? backgroundAlt || slot.alt || 'Property hero image'
    : slot.alt || backgroundAlt || 'Property hero image';

  return (
    <div className="hero-feature">
      <div className="hero-feature-frame">
        <EditableSlotGroup
          groupPath="hero.feature_image"
          definition={featureImageDef}
          className="hero-feature-slide is-active"
        >
          {image ? (
            <VibesitesImage
              url={image}
              alt={alt || ''}
              objectFit="cover"
              className="hero-feature-img"
              sizes="(min-width: 1024px) min(36vw, 500px), (min-width: 768px) 26rem, 22rem"
            />
          ) : (
            <div className="hero-feature-img hero-feature-img--empty" />
          )}
        </EditableSlotGroup>
      </div>
    </div>
  );
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  useComfyCtaStyles();

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="rubik"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;600;700;800;900&display=swap';
    link.setAttribute('data-vibe-font', 'rubik');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section relative w-full">
      {/* ---- Top photo band: background media + big headline ---- */}
      <div className="hero-band relative w-full overflow-hidden">
        <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
          {showVideoBackground && videoInfo ? (
            <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
              {bgImage.image && (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoInfo.type === 'embed'
                      ? iframeLoaded
                        ? 'opacity-0'
                        : 'opacity-100'
                      : videoReady
                        ? 'opacity-0'
                        : 'opacity-100'
                  }`}
                  sizes="100vw"
                />
              )}
              {videoInfo.type === 'embed' ? (
                <iframe
                  src={videoInfo.src}
                  className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                    iframeLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  allow="autoplay; fullscreen"
                  frameBorder="0"
                  onLoad={() => setIframeLoaded(true)}
                  title="Background video"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={videoInfo.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    videoReady ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              )}
            </div>
          ) : (
            /* Parallax + Ken Burns animated background */
            <div
              ref={imgWrapRef}
              className="hero-bg-media absolute h-[140%]"
              style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
            >
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  className="h-full w-full object-cover"
                  sizes="100vw"
                  style={{
                    animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                    willChange: 'transform',
                    transformOrigin: 'bottom right',
                    imageRendering: 'high-quality' as any,
                  }}
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-text)]" />
              )}
            </div>
          )}
        </EditableSlotGroup>

        {/* Gradient overlay for headline contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-black/30" />

        {/* Headline — left-aligned, sits over the photo. The editable property
            name is the entire h1; anything above/before it must be authored via
            hero.title so per-site overrides render correctly. */}
        <div className="hero-band-inner relative z-10">
          <RichTextContent
            path="hero.title"
            as="h1"
            className="hero-entrance hero-entrance-1 hero-title"
          />
        </div>
      </div>

      {/* ---- Lower white band: subtitle + CTA on the left ---- */}
      <div className="hero-lower relative w-full">
        <div className="hero-lower-inner">
          <div className="hero-copy">
            <RichTextContent
              path="hero.subtitle"
              label="Subtitle"
              defaultValue="<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>"
              as="div"
              className="hero-entrance hero-entrance-2 hero-subtitle"
            />

            <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="hero-entrance hero-entrance-3 comfy-cta mt-7"
              >
                {ctaLink.label || 'Find Your Space'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* ---- Feature image: straddles the two bands on the right (lg+),
              stacks as a full-width block below on mobile ---- */}
      <FeatureImage backgroundImage={bgImage.image} backgroundAlt={bgImage.alt} />

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Split hero: a photographic top band with the headline, over a white
           lower band holding the subtitle + CTA, with a feature image
           straddling the two on the right (desktop). */
        .hero-section {
          isolation: isolate;
          --hero-max: 1440px;
          /* Band grows a bit taller than the raw title needs so the added top
             padding leaves the headline visually balanced (not crammed against
             the bottom of the photo band). */
          --hero-band-h: clamp(400px, 62vh, 620px);
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 1024px) {
          .hero-section { --hero-band-h: clamp(540px, 70vh, 840px); }
        }

        .hero-band {
          height: var(--hero-band-h);
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .hero-band-inner {
          max-width: var(--hero-max);
          margin: 0 auto;
          height: 100%;
          display: flex;
          align-items: center;
          /* Top padding = the sticky offer banner's height + generous breathing
             room so the title clears the site header comfortably. The flex
             container centers the title inside (band height − padding-top),
             so extra top padding pushes the headline downward while keeping
             it vertically balanced within the remaining space. */
          padding: calc(var(--offer-banner-height, 0px) + 7rem) 1.5rem 0;
        }
        @media (min-width: 768px) {
          .hero-band-inner {
            padding-left: 4.4rem;
            padding-right: 4.4rem;
            padding-top: calc(var(--offer-banner-height, 0px) + 9rem);
          }
        }
        @media (min-width: 1024px) {
          .hero-band-inner {
            padding-top: calc(var(--offer-banner-height, 0px) + 11rem);
          }
        }

        .hero-title {
          font-family: "Rubik", var(--vibe-font-heading), sans-serif;
          font-size: clamp(2.5rem, 11vw, 3.5rem);
          font-weight: 800;
          line-height: 0.98;
          letter-spacing: -0.01em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.35);
          margin: 0 auto;
          max-width: 13ch;
          text-align: center;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-title {
            font-size: clamp(3.5rem, 7vw, 5.25rem);
            margin: 0;
            text-align: left;
          }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: clamp(4.25rem, 5vw, 6rem); max-width: 11ch; }
        }

        .hero-lower {
          background: var(--vibe-color-background, #fff);
        }
        .hero-lower-inner {
          max-width: var(--hero-max);
          margin: 0 auto;
          padding: 2.25rem 1.5rem 2.5rem;
        }
        @media (min-width: 768px) {
          .hero-lower-inner {
            padding-left: 4.4rem;
            padding-right: 4.4rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-lower { min-height: 250px; }
          /* Extra bottom room so the next section (PropertyOverview) — whose
             card pulls up with a -5rem overlap — lands on whitespace instead of
             covering the CTA. */
          .hero-lower-inner { padding-top: 3rem; padding-bottom: 8rem; }
        }
        .hero-copy {
          max-width: 34rem;
          margin: 0 auto;
          text-align: center;
        }
        @media (min-width: 768px) {
          .hero-copy { margin: 0; text-align: left; }
        }
        @media (min-width: 1024px) {
          .hero-copy { max-width: 46%; }
        }
        .hero-subtitle {
          font-family: var(--vibe-font-body), sans-serif;
          font-size: 1.0625rem;
          font-weight: 400;
          line-height: 1.6;
          color: var(--vibe-color-text);
          margin: 0 auto;
          max-width: 42ch;
        }
        @media (min-width: 768px) {
          .hero-subtitle { margin: 0; }
        }
        .hero-subtitle p { margin: 0; }
        .hero-subtitle p + p { margin-top: 0.75rem; }

        /* Feature image — mobile: a centered block flowing below the copy,
           narrower than full-bleed so the frame feels lighter. Keep the bottom
           padding tight so the next section sits close. */
        .hero-feature {
          display: block;
          width: 100%;
          max-width: 22rem;
          margin: 0 auto;
          padding: 0 1.5rem 2rem;
        }
        @media (min-width: 768px) {
          .hero-feature { max-width: 26rem; padding: 0 4.4rem 2.5rem; }
        }
        .hero-feature-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22);
        }
        .hero-feature-slide {
          position: absolute;
          inset: 0;
          opacity: 1;
        }
        .hero-feature-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .hero-feature-img--empty {
          background: var(--vibe-color-surface);
        }

        /* Desktop: absolutely position the feature image so it straddles the
           photo band and the white band, aligned to the content container's
           right edge. */
        @media (min-width: 1024px) {
          .hero-feature {
            position: absolute;
            z-index: 15;
            max-width: none;
            margin: 0;
            padding: 0;
            top: calc(var(--hero-band-h) * 0.45);
            width: min(36vw, 500px);
            aspect-ratio: 1 / 1;
            /* Right-anchored to the content container. The extra
               min(10vw, 140px) term nudges the frame leftward by the same
               amount the width was reduced (from min(46vw, 640px) to
               min(36vw, 500px)), so the left edge lands near its pre-resize
               position instead of drifting rightward with the smaller size. */
            right: calc(max(2.5rem, calc((100% - var(--hero-max)) / 2 + 4.4rem)) + min(10vw, 140px));
          }
          .hero-feature-frame {
            height: 100%;
            aspect-ratio: auto;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Discover thoughtfully designed apartment homes built for the way you want to live.</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.feature_image': featureImageDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
